import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Labeled, Modal, TextInput } from '@tes/ui';
import { WEBHOOK_EVENTS } from '@tes/domain';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import { useFormatDate } from '../lib/useSettings';
import type { WebhookDeliveryStatus, WebhookEndpointRow, WebhookEvent } from '../lib/types';

const DELIVERY_STYLE: Record<WebhookDeliveryStatus, string> = {
  delivered: 'text-emerald-600',
  failed: 'text-red-600',
  pending: 'text-amber-600',
  delivering: 'text-sky-600',
};

export default function WebhooksPage() {
  const qc = useQueryClient();
  const endpoints = useQuery({ queryKey: ['webhooks'], queryFn: api.listWebhooks });
  const deliveries = useQuery({ queryKey: ['deliveries'], queryFn: api.listDeliveries, refetchInterval: 5000 });
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpointRow | null>(null);
  const [freshSecret, setFreshSecret] = useState<string | null>(null);
  const fmt = useFormatDate();

  const del = useMutation({
    mutationFn: (id: string) => api.deleteWebhook(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const failedCount = (deliveries.data ?? []).filter((d) => d.status === 'failed').length;
  const error = endpoints.error ?? del.error;

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-5 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Webhook endpoints</h1>
          <Button onClick={() => setShowNew(true)}>
            <Plus size={16} className="mr-1" /> New endpoint
          </Button>
        </div>

        {freshSecret && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="mb-1 text-sm font-medium text-emerald-800">
              HMAC secret (shown once) — configure this in n8n to verify X-Signature.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-white px-2 py-1 font-mono text-xs">{freshSecret}</code>
              <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(freshSecret)} aria-label="Copy secret">
                <Copy size={14} />
              </Button>
            </div>
          </div>
        )}

        {error && <p className="mb-3 text-sm text-red-600">{messageOf(error, 'Something went wrong')}</p>}

        {endpoints.isLoading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !endpoints.data || endpoints.data.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-400">
            No webhook endpoints configured.
          </div>
        ) : (
          <div className="space-y-2">
            {endpoints.data.map((e) => (
              <div key={e.id} className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-4">
                <div className="min-w-0">
                  <div className="truncate font-mono text-sm">{e.url}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.events.map((ev) => (
                      <span key={ev} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                        {ev}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">Added {fmt.date(e.createdAt)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(e)}
                    className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                    aria-label={`Edit events for ${e.url}`}
                    title="Edit events (keeps the secret)"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => del.mutate(e.id)}
                    disabled={del.isPending}
                    className="rounded p-1 text-red-500 hover:bg-red-50"
                    aria-label={`Delete ${e.url}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-3 flex items-center gap-3">
          <h2 className="text-base font-semibold">Recent deliveries</h2>
          {failedCount > 0 && (
            <span className="flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
              <AlertTriangle size={13} /> {failedCount} failed
            </span>
          )}
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2">Event</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Attempts</th>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(deliveries.data ?? []).slice(0, 30).map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-2 font-mono text-[12px]">{d.event}</td>
                  <td className={`px-4 py-2 font-medium ${DELIVERY_STYLE[d.status] ?? ''}`}>{d.status}</td>
                  <td className="px-4 py-2">{d.attempts}</td>
                  <td className="px-4 py-2 text-slate-500">{d.responseCode ?? '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500" title={fmt.timestamp(d.createdAt)}>
                    {fmt.time(d.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditWebhookDialog
          endpoint={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ['webhooks'] });
          }}
        />
      )}

      {showNew && (
        <NewWebhookDialog
          onClose={() => setShowNew(false)}
          onCreated={(secret) => {
            setShowNew(false);
            setFreshSecret(secret);
            void qc.invalidateQueries({ queryKey: ['webhooks'] });
          }}
        />
      )}
    </div>
  );
}

function NewWebhookDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (secret: string) => void }) {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>([...WEBHOOK_EVENTS]);

  const mutation = useMutation({
    mutationFn: () => api.createWebhook(url, events),
    onSuccess: (r) => onCreated(r.secret),
  });
  const toggle = (e: WebhookEvent) =>
    setEvents((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));

  return (
    <Modal title="New webhook endpoint" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Labeled label="URL">
          <TextInput
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://n8n.example.com/webhook/tes"
            data-autofocus
          />
        </Labeled>
        <EventChecklist events={events} onToggle={toggle} />
        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Failed to create endpoint.')}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || events.length === 0}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EventChecklist({ events, onToggle }: { events: WebhookEvent[]; onToggle: (e: WebhookEvent) => void }) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-slate-500">Events</span>
      <div className="space-y-1">
        {WEBHOOK_EVENTS.map((e) => (
          <label key={e} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={events.includes(e)}
              onChange={() => onToggle(e)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600"
            />
            <code className="text-xs">{e}</code>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Edits subscriptions in place. This exists because the alternative —
 * delete and recreate — rotates the HMAC secret, which silently breaks the
 * receiver's signature check until someone notices the 401s.
 */
function EditWebhookDialog({
  endpoint,
  onClose,
  onSaved,
}: {
  endpoint: WebhookEndpointRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [events, setEvents] = useState<WebhookEvent[]>(endpoint.events);
  const mutation = useMutation({
    mutationFn: () => api.updateWebhook(endpoint.id, { events }),
    onSuccess: onSaved,
  });
  const toggle = (e: WebhookEvent) =>
    setEvents((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));

  return (
    <Modal title="Edit webhook events" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="truncate rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600" title={endpoint.url}>
          {endpoint.url}
        </div>
        <EventChecklist events={events} onToggle={toggle} />
        <p className="text-xs text-slate-500">The endpoint's secret is unchanged — n8n keeps verifying with the one it has.</p>
        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Failed to update endpoint.')}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || events.length === 0}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
